import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../../components/ui/Button';
import Select from 'react-select';
import { useAuth } from '../../../contexts/AuthContext';
import { useItems, Item } from '../../../hooks/useItems';
import { useCategories } from '../../../hooks/useCategories';

interface Category {
  id: string;
  name: string;
}

interface ItemFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  item: Item | null; // null for new item
  categories: Category[];
  initialName?: string;
  initialRate?: number;
  onSaved?: (item: Item) => void;
}

export default function ItemFormDrawer({ item, isOpen, onClose, onSaved, initialName, initialRate, categories }: ItemFormDrawerProps) {
  console.log('ItemFormDrawer: render called with item:', item);
  console.log('ItemFormDrawer: item?.id =', item?.id);
  const { token, currentBusiness, API_BASE_URL } = useAuth();
  const { createItem, updateItem } = useItems();
  const { mutate: refreshCategories } = useCategories('item');
  
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    rate: string;
    is_taxable: boolean;
    category_id: string | null;
    use_customer_rate: boolean;
  }>({
    name: '',
    description: '',
    rate: '',
    is_taxable: true,
    category_id: null,
    use_customer_rate: false,
  });

  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [showCategoryPopup, setShowCategoryPopup] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);

  const nameInputRef = useRef<HTMLInputElement>(null);

  const createCategory = async (name: string): Promise<Category | null> => {
    try {
      const res = await fetch(`${API_BASE_URL}/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name, type: 'item' })
      });
      if (!res.ok) throw new Error('Failed to create category');
      const newCategory = await res.json();
      
      // Refresh the categories list
      await refreshCategories();
      
      return newCategory;
    } catch (e) {
      alert('Failed to create category');
      return null;
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    
    setIsCreatingCategory(true);
    try {
      const created = await createCategory(newCategoryName.trim());
      if (created) {
        setFormData({ ...formData, category_id: created.id });
        setShowCategoryPopup(false);
        setNewCategoryName('');
      }
    } catch (error) {
      console.error('Failed to create category:', error);
    } finally {
      setIsCreatingCategory(false);
    }
  };

  // Reset form when item changes
  useEffect(() => {
    console.log('ItemFormDrawer: item prop changed:', item);
    if (item) {
      const newFormData = {
        name: item.name,
        description: item.description || '',
        rate: item.rate.toString(), // convert to string
        is_taxable: item.is_taxable,
        category_id: item.category_id || null,
        use_customer_rate: item.use_customer_rate || false,
      };
      console.log('ItemFormDrawer: setting form data:', newFormData);
      setFormData(prev => JSON.stringify(prev) === JSON.stringify(newFormData) ? prev : newFormData);
    } else {
      const newFormData = {
        name: initialName || '',
        description: '',
        rate: initialRate != null ? String(initialRate) : '',
        is_taxable: true,
        category_id: null,
        use_customer_rate: false,
      };
      setFormData(prev => JSON.stringify(prev) === JSON.stringify(newFormData) ? prev : newFormData);
    }
    setError('');
    setSubmitAttempted(false);
  }, [item, initialName, initialRate]);

  // Auto-focus first field when drawer opens
  useEffect(() => {
    if (isOpen) setTimeout(() => nameInputRef.current?.focus(), 0);
  }, [isOpen]);

  // Document-level ESC handler
  useEffect(() => {
    if (!isOpen) return;
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', esc, { capture: true });
    return () => document.removeEventListener('keydown', esc, { capture: true });
  }, [isOpen, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted!', { formData, name: formData.name, rate: formData.rate });
    console.log('ItemFormDrawer: handleSubmit called with item:', item);
    console.log('ItemFormDrawer: item?.id =', item?.id);
    setIsSubmitting(true);
    setError('');
    setSubmitAttempted(true);

    const rateValue = Number(formData.rate);
    console.log('Validation check:', { 
      nameEmpty: !formData.name.trim(), 
      rateEmpty: !formData.rate, 
      rateZero: rateValue === 0,
      rateNegative: rateValue < 0
    });

    if (!formData.name.trim()) {
      setError('Name is required');
      setIsSubmitting(false);
      return;
    }

    if (!formData.rate) {
      setError('Rate is required');
      setIsSubmitting(false);
      return;
    }

    if (rateValue < 0) {
      setError('Rate cannot be negative');
      setIsSubmitting(false);
      return;
    }

    try {
      const submitData = {
        ...formData,
        rate: rateValue,
      };

      let saved: Item;
      if (item) {
        // Capture the item ID at the time of submission to avoid race conditions
        const itemId = item.id;
        console.log('ItemFormDrawer: captured itemId =', itemId);
        
        // Exclude id from payload; pass it in the path instead
        const updateData = {
          ...submitData,
          // Convert null to undefined for optional fields
          category_id: submitData.category_id || undefined
        };
        console.log('ItemFormDrawer: updating item with data:', updateData);
        console.log('ItemFormDrawer: item.id =', item.id);
        saved = await updateItem(itemId, updateData);
      } else {
        saved = await createItem(submitData as any);
      }
      if (saved && onSaved) onSaved(saved);
      onClose();
    } catch (err: any) {
      console.error('Failed to save item:', err);
      setError(err.message || 'Failed to save item');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
          <div className="w-screen max-w-md">
            <div className="fixed top-0 right-0 h-full w-full sm:w-[600px] bg-white shadow-2xl ring-1 ring-black/5 dark:bg-neutral-900">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-neutral-800">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {item ? 'Edit Item' : 'Add Item'}
                </h2>
                <div className="ml-3 h-7 flex items-center">
                  <button
                    type="button"
                    className="bg-white dark:bg-neutral-900 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close panel</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                <div className="px-6 py-6">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
                        <p className="text-red-800 dark:text-red-200">{error}</p>
                      </div>
                    )}

                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Name *
                      </label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        ref={nameInputRef}
                        aria-invalid={submitAttempted && !formData.name}
                        className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-sedra dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                        placeholder="Item name"
                      />
                    </div>

                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={3}
                        className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-sedra dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                        placeholder="Optional description"
                      />
                    </div>

                    <div>
                      <label htmlFor="rate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Rate *
                      </label>
                      <input
                        id="rate"
                        name="rate"
                        type="number"
                        value={formData.rate}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                        aria-invalid={submitAttempted && (!formData.rate || Number(formData.rate) === 0)}
                        className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-sedra dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label htmlFor="category_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Category
                      </label>
                      <div className="rounded-xl ring-1 ring-gray-300 focus-within:ring-2 focus-within:ring-sedra dark:ring-neutral-700" onClick={() => console.log('[ItemFormDrawer] Category wrapper clicked')}>
                        <Select
                          id="category_id"
                          name="category_id"
                          value={formData.category_id ? categories.find((cat: Category) => cat.id === formData.category_id) || null : null}
                          onChange={(option: any) => {
                            console.log('[ItemFormDrawer] Category selected via react-select:', option);
                            const newFormData = { 
                              ...formData, 
                              category_id: option ? option.id : null 
                            };
                            console.log('[ItemFormDrawer] Updated form data:', newFormData);
                            setFormData(newFormData);
                          }}
                          options={categories}
                          getOptionLabel={(option: Category) => option.name}
                          getOptionValue={(option: Category) => option.id}
                          isClearable
                          placeholder="Select a category"
                          onMenuOpen={() => console.log('[ItemFormDrawer] Menu opened, categories:', categories)}
                          onMenuClose={() => console.log('[ItemFormDrawer] Menu closed')}
                          onInputChange={(inputValue) => console.log('[ItemFormDrawer] Input changed:', inputValue)}
                          onFocus={() => console.log('[ItemFormDrawer] Select focused')}
                          onBlur={() => console.log('[ItemFormDrawer] Select blurred')}
                          noOptionsMessage={({ inputValue }: { inputValue: string }) => (
                            <div>
                              No categories found.
                              <button
                                type="button"
                                className="ml-2 text-blue-600 underline text-xs"
                                onClick={() => {
                                  setShowCategoryPopup(true);
                                  setNewCategoryName(inputValue);
                                }}
                              >
                                Add new category
                              </button>
                            </div>
                          )}
                          isSearchable={true}
                          isDisabled={false}
                        />
                      </div>
                    </div>

                    {/* Category Creation Popup Modal */}
                    {showCategoryPopup && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9998]">
                        <div className="rounded-2xl border border-gray-200 bg-white p-5 elev-1 dark:bg-neutral-900 dark:border-neutral-800 w-full max-w-md mx-4">
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                            Create New Category
                          </h3>
                          
                          <div className="mb-4">
                            <label htmlFor="new-category-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Category Name *
                            </label>
                            <input
                              id="new-category-name"
                              type="text"
                              value={newCategoryName}
                              onChange={(e) => setNewCategoryName(e.target.value)}
                              className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-sedra dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
                              placeholder="Enter category name"
                              autoFocus
                            />
                          </div>

                          <div className="flex justify-end space-x-3">
                            <Button
                              type="button"
                              variant="secondary"
                              onClick={() => {
                                setShowCategoryPopup(false);
                                setNewCategoryName('');
                              }}
                              disabled={isCreatingCategory}
                              className="inline-flex items-center justify-center rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra dark:text-gray-200 dark:border-neutral-700 dark:hover:bg-neutral-800"
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              onClick={handleCreateCategory}
                              disabled={isCreatingCategory || !newCategoryName.trim()}
                              className="inline-flex items-center justify-center rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sedra-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              {isCreatingCategory ? 'Creating...' : 'Create Category'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-3">
                      <input
                        id="is_taxable"
                        name="is_taxable"
                        type="checkbox"
                        checked={formData.is_taxable}
                        onChange={handleChange}
                        className="h-4 w-4 rounded border-gray-300 text-sedra focus:ring-sedra"
                      />
                      <label htmlFor="is_taxable" className="block text-sm text-gray-900 dark:text-gray-300">
                        Taxable Item
                      </label>
                      <input
                        id="use_customer_rate"
                        name="use_customer_rate"
                        type="checkbox"
                        checked={formData.use_customer_rate}
                        onChange={handleChange}
                        className="h-4 w-4 rounded border-gray-300 text-sedra focus:ring-sedra"
                      />
                      <label htmlFor="use_customer_rate" className="block text-sm text-gray-900 dark:text-gray-300">
                        Use customer-specific rate
                      </label>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="inline-flex items-center justify-center rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra dark:text-gray-200 dark:border-neutral-700 dark:hover:bg-neutral-800"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="inline-flex items-center justify-center rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sedra-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? 'Saving...' : (item ? 'Update' : 'Create')}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 